/**
 * The workspace lives in the address: `flashroom.flashsuite.ai/<workspace>/rooms`.
 * The first path segment names the workspace by its subdomain slug, unless it
 * is one of the app's own pages or a public route. The API reads the same slug
 * off the `x-workspace` header (or `?ws=` for a WebSocket) and scopes the
 * request to the account's seat in that workspace.
 */

/** First path segments that are the app's own or a public page, never a workspace. */
const RESERVED_PATHS: ReadonlySet<string> = new Set([
  "api",
  "assets",
  "w",
  "watch",
  "login",
  "invite",
  "live",
  "reset",
  "welcome",
  "home",
  "rooms",
  // Old addresses still arrive from emails and bookmarks, and redirect to /rooms.
  "webinars",
  "people",
  "automations",
  "conversations",
  "settings",
  // First-run setup, and the records page a business sends whoever runs its website.
  "setup",
  "records",
  "account",
  // The phone's More tab: a bare /more must open it, not ask for a workspace named "more".
  "more",
  "app",
  "www",
  "demo",
  "admin",
  // Flashsuite's routes on the app (/flashsuite/events, /flashsuite/switch).
  "flashsuite",
  // Our own hosts on flashsuite.net, which no workspace may take as its address.
  "videos",
  "replies",
  "connect",
  "cdn",
  "mail",
  "favicon.ico",
  "robots.txt",
  "manifest.webmanifest",
]);

export const WORKSPACE_HEADER = "x-workspace";
export const WORKSPACE_QUERY = "ws";

const SLUG = /^[a-z0-9][a-z0-9-]*$/;

/** A slug nobody may take as a workspace address. */
export function isReservedSlug(slug: string): boolean {
  return RESERVED_PATHS.has(slug.toLowerCase());
}

/** The workspace named by a path, or null when the path is the app's own. */
export function workspaceSlugFromPath(pathname: string): string | null {
  const first = pathname.split("/").filter(Boolean)[0]?.toLowerCase() ?? "";
  if (!first || !SLUG.test(first) || RESERVED_PATHS.has(first)) return null;
  return first;
}

/** The rest of a path once the workspace is taken off the front. */
export function pathWithoutWorkspace(pathname: string): string {
  const slug = workspaceSlugFromPath(pathname);
  if (!slug) return pathname;
  const rest = pathname.slice(slug.length + 1);
  return rest.startsWith("/") ? rest : `/${rest}`;
}
